/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */
import { VerificationStatusResponse, VerificationStatusResult, } from 'types/responses/VerificationStatusResponse'
import BookingAction from '../store/booking/BookingAction'
import { DEFAULT_HALL, DEFAULT_HALL_ID, DEFAULT_RESERVATION_DAYS_FROM_NOW, IS_UPSELL_ALLOWED, } from 'helpers/config'
import { PackagesCart } from 'types/entities/PackagesCart'
import type { ZodTypeAny } from 'zod'
import { getNewStep, getNewStepGift } from './utility'
import { VerificationType } from 'types/enums/VerificationType'
import { PackagesCartItemSlot } from 'types/entities/PackagesCartItemSlot'
import { PackageCategory } from 'types/enums/PackageCategory'
import { HallCode } from 'types/enums/HallCode'
import { BookingNavigationStep } from 'store/booking/BookingReducer'
import { useTypedSelector } from 'helpers/reducers'
import { useEffect, useRef } from 'react'
import Dates from 'utils/Dates'
import { getVoucherCodes } from 'utils/voucherUtils'
import { getQueryParams } from 'helpers/helpers'
import { useNavigate } from 'react-router-dom'
import { useDebouncedDispatch } from 'utils/useDebounceQueue'
import { RouteName } from 'types/enums/RouteName'
import { hasCreditOrUpsellItems } from 'utils/offers/addItemToCalculationUtils'
import { useAppDispatch } from 'helpers/store'
import { Root } from 'react-dom/client'

export const useValueVerification = (packagesCart: PackagesCart) => {
    const dispatch = useAppDispatch()

    const sendVerification = (
        value: string,
        type: VerificationType
    ): Promise<VerificationStatusResponse> => {
        return new Promise((resolve, reject) => {
            dispatch(
                BookingAction.fetchVerification(
                    {
                        value: value,
                        hall_id: DEFAULT_HALL_ID,
                        type,
                    },
                    packagesCart as PackagesCart
                )
            )
                .then((r) => resolve(r))
                .catch((err) => reject(err))
        })
    }

    const createContainer = () => {
        const container = document.createElement('div')
        const holder = document.getElementById(
            'verification-code-container'
        ) as HTMLElement
        holder.appendChild(container)
        return container
    }

    const handleVerification = (
        data_2: {
            request_id: string
            verification_code: string
            type: VerificationType
        },
        resultVerification: VerificationStatusResponse
    ): Promise<VerificationStatusResult> => {
        return new Promise((resolve, reject) => {
            if (
                data_2.request_id === resultVerification.verification_request_id
            ) {
                dispatch(
                    BookingAction.fetchSubmitVerificationCode(
                        data_2,
                        packagesCart as PackagesCart
                    )
                ).then((r_1) => {
                    if (r_1.result) resolve(r_1)
                    else reject(false)
                })
            } else {
                reject(false)
            }
        })
    }

    const closeVerificationWindow = ({
        container,
        root,
    }: {
        container: HTMLElement
        root: Root
    }) => {
        root.unmount()
        container.remove()
    }

    return {
        sendVerification,
        createContainer,
        handleVerification,
        closeVerificationWindow,
    }
}

export const useValueVerificationForShop = () => {
    const dispatch = useAppDispatch()

    const sendVerification = (
        value: string,
        type: VerificationType
    ): Promise<VerificationStatusResponse> => {
        return new Promise((resolve, reject) => {
            dispatch(
                BookingAction.fetchVerificationForShop({
                    value,
                    hall_id: DEFAULT_HALL_ID,
                    type,
                })
            )
                .then((r) => resolve(r))
                .catch((err) => reject(err))
        })
    }

    const createContainer = () => {
        const container = document.createElement('div')
        const holder = document.getElementById(
            'verification-code-container'
        ) as HTMLElement
        holder.appendChild(container)
        return container
    }

    const handleVerification = (
        data_2: {
            request_id: string
            verification_code: string
            type: VerificationType
        },
        resultVerification: VerificationStatusResponse
    ): Promise<boolean> => {
        return new Promise((resolve, reject) => {
            if (
                data_2.request_id === resultVerification.verification_request_id
            ) {
                dispatch(
                    BookingAction.fetchSubmitVerificationCodeForShop(data_2)
                ).then((r_1) => {
                    if (r_1.result) resolve(true)
                    else reject(false)
                })
            } else {
                reject(false)
            }
        })
    }

    const closeVerificationWindow = ({
        container,
        root,
    }: {
        container: HTMLElement
        root: Root
    }) => {
        root.unmount()
        container.remove()
    }

    return {
        sendVerification,
        createContainer,
        handleVerification,
        closeVerificationWindow,
    }
}

export const itemSlots: (
    packagesCartItemSlots: PackagesCartItemSlot[],
    packageCategory: PackageCategory
) => PackagesCartItemSlot[] = (packagesCartItemSlots, packageCategory) => {
    return packagesCartItemSlots
        .filter((_itemSlot, index) =>
            packageCategory === PackageCategory.Event &&
            DEFAULT_HALL !== HallCode.Zwolle
                ? index === 0
                : true
        )
        .reduce((acc: PackagesCartItemSlot[], itemSlot) => {
            const existingSlot = acc.find(
                (slot) => slot.timestamp === itemSlot.timestamp
            )
            if (existingSlot) {
                existingSlot.persons_count += itemSlot.persons_count
            } else {
                acc.push({ ...itemSlot })
            }
            return acc
        }, [])
}

export const useRequiredField = (schema: { shape?: Record<string, ZodTypeAny> } | null | undefined) => {
    const testField = (fieldName: string) => {
        if (!schema) return false

        // Zod support
        if (schema.shape) {
            const field = schema.shape[fieldName]
            if (!field) return false
            // Check if it's NOT an optional/nullable type
            return (
                typeof field.isOptional === 'function' &&
                !field.isOptional()
            )
        }

        return false
    }

    return {
        testField,
    }
}

export const useNavigationSteps = () => {
    const dispatch = useAppDispatch()
    const bookingState = useTypedSelector((state) => state.bookingState)

    const setCurrentStep = (step: BookingNavigationStep) => {
        dispatch(BookingAction.setCurrentNavigationStep(step))
    }

    const proceedIfTimeslotsAllowed = (routeName: RouteName): void => {
        const availablePackages =
            bookingState?.bookingProcess?.availablePackageItems
        const availablePackagesHasUpsell = hasCreditOrUpsellItems(availablePackages)

        switch (routeName) {
            case RouteName.Main:
                return setCurrentStep(getNewStep(bookingState, 1))
            case RouteName.Options:
                return setCurrentStep(getNewStep(bookingState, 2))
            case RouteName.TimeslotsNew:
                return setCurrentStep(getNewStep(bookingState, 3))
            case RouteName.Packages:
                return setCurrentStep(getNewStep(bookingState, 4))
            case RouteName.UpsellsNew:
                return setCurrentStep(getNewStep(bookingState, 5))
            case RouteName.Customer:
                return setCurrentStep(
                    getNewStep(
                        bookingState,
                        IS_UPSELL_ALLOWED
                            ? availablePackagesHasUpsell
                                ? 6
                                : 5
                            : 5
                    )
                )
            case RouteName.Paid:
                return setCurrentStep(
                    getNewStep(
                        bookingState,
                        IS_UPSELL_ALLOWED
                            ? availablePackagesHasUpsell
                                ? 7
                                : 6
                            : 6
                    )
                )
            case RouteName.Gift:
                return setCurrentStep(getNewStepGift(bookingState, 2))
            case RouteName.GiftCheckout:
                return setCurrentStep(getNewStepGift(bookingState, 3))
        }
    }

    const proceedIfTimeslotsNotAllowed = (location: string): void => {
        if (location.includes('package')) {
            setCurrentStep(getNewStep(bookingState, 1))
        } else if (location.includes('upsell')) {
            setCurrentStep(getNewStep(bookingState, 2))
        } else if (location.includes('customer')) {
            setCurrentStep(getNewStep(bookingState, 3))
        }
    }

    const updateSteps = (pathName: RouteName) => {
        return bookingState.timeslotsAllowed
            ? proceedIfTimeslotsAllowed(pathName)
            : proceedIfTimeslotsNotAllowed(pathName)
    }

    return {
        proceedIfTimeslotsAllowed,
        proceedIfTimeslotsNotAllowed,
        updateSteps,
    }
}

export const useHallOpening = () => {
    return {
        // Mock implementation until actual hook is found
        isOpen: () => true,
    }
}

export const useQueryParams = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const bookingState = useTypedSelector((state) => state.bookingState)
    const vouchers = useTypedSelector(
        (state) => state.voucherState.voucherItems
    )
    const bookingFilter = bookingState.bookingProcess.filter
    const dateChosenByCustomer =
        bookingState.bookingProcess.dateChosenByCustomer

    const queryParams = new URLSearchParams(location.search as any)
    const currentRouteParams = useRef(queryParams.toString())
    const { debouncedDispatch } = useDebouncedDispatch()

    const fetchPackagesByLocation = () => {
        if (
            !location.pathname.includes('customer') &&
            !location.pathname.includes('booking/book')
        ) {
            const voucherCodes = getVoucherCodes(vouchers)
            return debouncedDispatch(
                BookingAction.fetchPackagesByLocationParams(
                    location,
                    bookingFilter,
                    dateChosenByCustomer,
                    voucherCodes
                )
            )
        }
    }

    const fetchPackagesForHall = () => {
        const date = Dates.ymd(bookingFilter.date) || Dates.ymd(new Date())
        return debouncedDispatch(BookingAction.fetchPackagesForHall(date))
    }

    const historyFromCurrentRouteParams = () => {
        const currentRouteParamsObj: any = Object.fromEntries(
            queryParams.entries()
        )

        if (
            !Dates.isToday(new Date(bookingFilter.date)) ||
            !Dates.isToday(new Date(currentRouteParamsObj.date))
        ) {
            const filterParams = changeQueryParams()
            const routeParams = new URLSearchParams(
                filterParams as any
            ).toString()
            const path = location.pathname.includes('booking/book')
                ? '/booking'
                : location.pathname
            navigate(path + '?' + routeParams)
        }
    }

    const setFilterFromQueryParams = () => {
        if (getQueryParams()) {
            dispatch(
                BookingAction.setFilter({
                    ...bookingFilter,
                    ...getQueryParams(),
                })
            )
        }
    }

    const changeQueryParamsIfUpdated = () => {
        const routeParams = new URLSearchParams(bookingFilter as any).toString()

        if (currentRouteParams.current === routeParams) return

        currentRouteParams.current = routeParams

        const path = location.pathname
        navigate(path + '?' + routeParams, { replace: true })
    }

    useEffect(() => {
        historyFromCurrentRouteParams()
    }, [])

    useEffect(() => {
        setFilterFromQueryParams()
    }, [])

    useEffect(() => {
        changeQueryParamsIfUpdated()
    }, [bookingFilter])

    const changeQueryParams = () => {
        const minimalDatePreset = new Date(
            Date.now() + DEFAULT_RESERVATION_DAYS_FROM_NOW * 24 * 60 * 60 * 1000
        )

        const queryParams = new URLSearchParams(location.search)
        const filterParams: any = Object.fromEntries(queryParams.entries())
        if (!filterParams) return

        const isFilterDate = filterParams.date
        const isOldDate =
            typeof isFilterDate === 'undefined'
                ? true
                : new Date(filterParams.date).getTime() <
                  new Date(minimalDatePreset).getTime()

        filterParams.adults = filterParams.adults || 0
        filterParams.children = filterParams.children || 0
        filterParams.date = isOldDate
            ? Dates.ymd(new Date(minimalDatePreset))
            : filterParams.date
        filterParams.activity_ids = filterParams.activity_ids || []
        filterParams.groupType = filterParams.groupType || []
        filterParams.hours_count = filterParams.hours_count || 0

        dispatch(BookingAction.setFilter(filterParams))

        return filterParams
    }

    return {
        changeQueryParams,
        changeQueryParamsIfUpdated,
        fetchPackagesByLocation,
        fetchPackagesForHall,
        setFilterFromQueryParams,
    }
}
